// LUM-525 web-click attribution. Calls from /local pages attribute through
// the dedicated TFN; web clicks attribute through a qual carrying the Local
// Pages promoId. The funnel's /qualify entry (src/pages/qualify.tsx) decodes
// ?qual=<base64 JSON> into the Qual store, and the promoId resolves the
// microsite via promoIdToMicrosite — so a promoId-only qual is a complete,
// valid entry. A bare cart link carries an empty qual and attributes to the
// funnel default, which is exactly the leak this closes.
//
// LUM-566: the qual also carries gtmCode. The funnel's GoogleTagManager
// reads qual.gtmCode, and useAsyncCheckoutUrl spreads the whole qual into
// the async-buyflow reservation token. The XfinitySpecial microsite has no
// config fallback (static config has none, no remote config exists for this
// promoId) — without it, GTM never starts past the /local pages.

import { GTM_ID } from 'components/SeoGeoLocal/tracking';

const CART_FUNNEL_URL = 'https://cart.xfinityspecial.com/provider';

/** Address shape the qual carries when the /local modal has verified one.
 * Field names match the funnel's Qual type (`zip` — /qualify normalizes it
 * to `postalCode` on decode). Values come from Smarty / updater-api, which
 * return ASCII — required by the plain-base64 encode below. */
export interface CartQualAddress {
  street: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
}

function toBase64(json: string): string {
  // Static CTAs encode at build (Node); the address modal encodes at click
  // time (browser). Same plain base64 the WordPress qualform produces —
  // /qualify decodes with atob, so the input must stay Latin-1. Reject
  // non-Latin-1 up front in BOTH environments: btoa would throw anyway,
  // and Buffer's latin1 encoding would silently truncate instead.
  if (!/^[\u0000-\u00ff]*$/.test(json)) {
    throw new Error('cartLink: qual contains non-Latin-1 characters');
  }
  return typeof window === 'undefined'
    ? Buffer.from(json, 'latin1').toString('base64')
    : window.btoa(json);
}

/**
 * Funnel entry URL with a qual attributing the visit to `promoId`.
 * Fail-closed: pages call this at module scope from snapshot/targets data,
 * so a re-ingest that loses the promoId breaks the build instead of
 * shipping CTAs that silently attribute to the default promo.
 */
export function cartQualifyUrl(
  promoId: string | undefined,
  address?: CartQualAddress
): string {
  if (!promoId) {
    throw new Error(
      'cartQualifyUrl: promoId is missing — web clicks would attribute to the default promo'
    );
  }
  if (address) {
    const qual = {
      promoId,
      gtmCode: GTM_ID,
      street: address.street,
      // JSON.stringify drops undefined, so a missing unit never
      // serializes as the string "undefined".
      unit: address.unit || undefined,
      city: address.city,
      state: address.state,
      zip: address.zip,
    };
    try {
      return qualifyUrlFor(qual);
    } catch {
      // A non-Latin-1 address must not white-screen the confirmed modal —
      // fall back to the promoId-only qual: attribution still lands, the
      // user just re-enters the address in the funnel.
    }
  }
  return qualifyUrlFor({ promoId, gtmCode: GTM_ID });
}

function qualifyUrlFor(qual: Record<string, string | undefined>): string {
  const encoded = encodeURIComponent(toBase64(JSON.stringify(qual)));
  return `${CART_FUNNEL_URL}/qualify?qual=${encoded}`;
}
